import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Shield } from 'lucide-react';

export function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-bold text-sm uppercase tracking-wider">Back to Home</span>
          </button>
          
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs font-black text-slate-600 uppercase tracking-widest">Legal Document</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Title Section */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            Terms of Service
          </h1>
          <div className="flex items-center justify-center gap-8 text-sm text-slate-500 font-semibold">
            <div>
              <span className="text-slate-400">Last Updated:</span> January 30, 2026
            </div>
            <div className="w-1 h-1 rounded-full bg-slate-300" />
            <div>
              <span className="text-slate-400">Effective Date:</span> January 30, 2026
            </div>
          </div>
          <div className="mt-4">
            <span className="inline-block px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-xs font-black uppercase tracking-widest">
              QHTECH SOLUTIONS L.L.C
            </span>
          </div>
        </div>

        {/* Content Sections */}
        <div className="prose prose-slate max-w-none">
          
          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">1</span>
              Agreement to Terms
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") 
              and QHTECH SOLUTIONS L.L.C ("Company," "we," "us," or "our") governing your access to and use of the DAGGPT 
              platform, including all services, features, content, and applications offered through 
              <a href="https://daggpt.network" className="text-blue-600 hover:text-blue-700 font-semibold"> https://daggpt.network</a> 
              (collectively, the "Platform").
            </p>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using the Platform, you acknowledge that you have read, understood, and agree to be bound by 
              these Terms and our Privacy Policy. If you do not agree to these Terms, you must immediately cease all use of 
              the Platform.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">2</span>
              Eligibility and Account Registration
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">2.1. Age Requirements</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              You must be at least 18 years of age to use the Platform. By using the Platform, you represent and warrant 
              that you meet this age requirement. We do not knowingly collect information from or direct any of our content 
              specifically to individuals under 18 years of age.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">2.2. Account Creation</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              To access certain features of the Platform, you must create an account by providing:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>A valid email address</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Your full legal name</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>A secure password meeting our security requirements</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">2.3. Account Security</h3>
            <p className="text-slate-600 leading-relaxed">
              You are solely responsible for maintaining the confidentiality of your account credentials and for all 
              activities that occur under your account. You agree to immediately notify us of any unauthorized use of 
              your account or any other breach of security at <a href="mailto:support@daggpt.network" className="text-blue-600 hover:text-blue-700 font-semibold">support@daggpt.network</a>.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">3</span>
              Platform Services and AI Generation
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.1. Service Description</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              DAGGPT provides access to advanced generative artificial intelligence models capable of creating:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Text Content:</strong> Articles, code, creative writing, and conversational responses</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Images:</strong> Digital artwork, photographs, and visual designs</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Videos:</strong> Animated sequences and video content</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Music:</strong> Audio compositions and soundtracks</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span><strong className="text-slate-900">Websites:</strong> Web page designs and layouts</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.2. Service Availability</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              While we strive to provide continuous access to the Platform, we do not guarantee uninterrupted service. 
              The Platform may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control. 
              We reserve the right to modify, suspend, or discontinue any aspect of the Platform at any time without prior notice.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">3.3. AI Model Limitations</h3>
            <p className="text-slate-600 leading-relaxed">
              You acknowledge that AI-generated content may contain inaccuracies, biases, or errors. The Platform's AI 
              models are trained on diverse datasets and may produce outputs that are factually incorrect, outdated, or 
              inappropriate. You are solely responsible for reviewing, verifying, and validating all AI-generated content 
              before use in any production environment.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-12 bg-blue-50/50 rounded-2xl p-8 border border-blue-100">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600 text-white text-sm font-black">4</span>
              Intellectual Property Rights
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">4.1. Platform Ownership</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              The Platform, including its software, algorithms, user interface, design, trademarks, logos, and all 
              related intellectual property, is owned by QHTECH SOLUTIONS L.L.C and protected by international copyright, 
              trademark, patent, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">4.2. User-Generated Content Ownership</h3>
            <p className="text-slate-600 leading-relaxed mb-4">
              Subject to Section 4.3, you retain all ownership rights to content you generate using the Platform 
              ("User Content"). However, by using the Platform, you grant us a worldwide, non-exclusive, royalty-free, 
              sublicensable license to:
            </p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Store and process your prompts and generated content to provide the service</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Use aggregated, anonymized data for model improvement and research</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Display User Content in marketing materials with your explicit consent</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">4.3. Third-Party IP Considerations</h3>
            <p className="text-slate-600 leading-relaxed">
              AI-generated content may inadvertently resemble existing copyrighted works. We do not claim ownership of 
              such pre-existing works, and you are responsible for ensuring your use of AI-generated content does not 
              infringe upon third-party intellectual property rights. We recommend conducting appropriate clearance 
              procedures before commercial use.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">5</span>
              Prohibited Uses and Content Policy
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">5.1. Prohibited Activities</h3>
            <p className="text-slate-600 leading-relaxed mb-4">You agree not to use the Platform to:</p>
            <ul className="space-y-2 text-slate-600 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Generate content that is illegal, harmful, threatening, abusive, harassing, defamatory, or invasive of privacy</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Create deepfakes, non-consensual intimate imagery, or content impersonating real individuals without authorization</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Generate content promoting violence, terrorism, or illegal activities</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Produce spam, phishing content, or malware</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Attempt to reverse-engineer, decompile, or extract the Platform's underlying models</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Use automated systems to excessively access the Platform (rate limiting applies)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-blue-600 font-bold mt-1">•</span>
                <span>Resell or redistribute Platform access without written authorization</span>
              </li>
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">5.2. Content Moderation</h3>
            <p className="text-slate-600 leading-relaxed">
              We employ automated and manual content moderation systems. We reserve the right to review, flag, or remove 
              any content that violates these Terms. Repeated violations may result in account suspension or termination.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">6</span>
              Payment Terms and Subscriptions
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.1. Pricing and Billing</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Certain features of the Platform require payment of subscription fees. All fees are stated in U.S. Dollars (USD) 
              and are non-refundable except as required by law or as explicitly stated in our refund policy. Subscription 
              fees are billed in advance on a monthly or annual basis, depending on your selected plan.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.2. Automatic Renewal</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Your subscription will automatically renew at the end of each billing period unless you cancel before the 
              renewal date. You may cancel your subscription at any time through your account settings or by contacting 
              support@daggpt.network.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">6.3. Price Changes</h3>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify our pricing structure at any time. Price changes will be communicated at 
              least 30 days in advance and will apply to subsequent billing periods. Continued use of the Platform after 
              a price change constitutes acceptance of the new pricing.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">7</span>
              Disclaimers and Limitation of Liability
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.1. "AS IS" Disclaimer</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR 
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES 
              OR OTHER HARMFUL COMPONENTS.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">7.2. Limitation of Liability</h3>
            <p className="text-slate-600 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, QHTECH SOLUTIONS L.L.C SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR 
              GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY 
              OF SUCH DAMAGES.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">8</span>
              Indemnification
            </h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify, defend, and hold harmless QHTECH SOLUTIONS L.L.C, its affiliates, officers, directors, 
              employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including 
              reasonable attorneys' fees) arising out of or related to: (a) your use of the Platform; (b) your violation 
              of these Terms; (c) your violation of any third-party rights, including intellectual property rights; or 
              (d) any content you generate or submit through the Platform.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">9</span>
              Dispute Resolution and Governing Law
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">9.1. Governing Law</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, 
              without regard to its conflict of law provisions.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">9.2. Arbitration Agreement</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Any dispute arising out of or relating to these Terms or the Platform shall be resolved through binding 
              arbitration administered by the Dubai International Arbitration Centre (DIAC) in accordance with its 
              arbitration rules. The arbitration shall be conducted in English in Dubai, UAE.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">9.3. Class Action Waiver</h3>
            <p className="text-slate-600 leading-relaxed">
              You agree that any arbitration or proceeding shall be limited to the dispute between you and QHTECH SOLUTIONS L.L.C 
              individually. You waive any right to participate in a class action lawsuit or class-wide arbitration.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">10</span>
              Termination
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">10.1. Termination by You</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              You may terminate your account at any time by contacting support@daggpt.network or through your account 
              settings. Upon termination, your right to access the Platform will immediately cease.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">10.2. Termination by Us</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              We reserve the right to suspend or terminate your account and access to the Platform at any time, with or 
              without cause, and with or without notice. Reasons for termination may include, but are not limited to, 
              violation of these Terms, fraudulent activity, or prolonged inactivity.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">10.3. Effect of Termination</h3>
            <p className="text-slate-600 leading-relaxed">
              Upon termination, all licenses and rights granted to you under these Terms will immediately cease. Sections 
              of these Terms that by their nature should survive termination (including intellectual property provisions, 
              disclaimers, and limitations of liability) shall remain in effect.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">11</span>
              Changes to Terms
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We reserve the right to modify these Terms at any time. Material changes will be communicated via email 
              to the address associated with your account or through a prominent notice on the Platform at least 30 days 
              before the changes take effect.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Your continued use of the Platform after the effective date of revised Terms constitutes your acceptance 
              of the changes. If you do not agree to the modified Terms, you must discontinue use of the Platform.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-12">
            <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 text-sm font-black">12</span>
              Miscellaneous Provisions
            </h2>
            
            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">12.1. Entire Agreement</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and QHTECH SOLUTIONS L.L.C 
              regarding the Platform and supersede all prior agreements and understandings.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">12.2. Severability</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall 
              remain in full force and effect.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">12.3. Waiver</h3>
            <p className="text-slate-600 leading-relaxed mb-6">
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right 
              or provision.
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3 mt-6">12.4. Assignment</h3>
            <p className="text-slate-600 leading-relaxed">
              You may not assign or transfer these Terms or your account without our prior written consent. We may assign 
              these Terms without restriction.
            </p>
          </section>

          {/* Section 13 - Contact */}
          <section className="mb-12 bg-slate-900 text-white rounded-2xl p-8">
            <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white text-slate-900 text-sm font-black">13</span>
              Contact Information
            </h2>
            <div className="space-y-4">
              <div>
                <p className="font-black text-lg mb-2">QHTECH SOLUTIONS L.L.C</p>
                <div className="flex items-start gap-3 text-slate-300">
                  <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                  <p className="leading-relaxed">
                    Meydan Grandstand, 6th floor, Meydan Road,<br />
                    Nad Al Sheba, Dubai, U.A.E.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-400" />
                <a href="mailto:support@daggpt.network" className="text-blue-400 hover:text-blue-300 font-semibold transition-colors">
                  support@daggpt.network
                </a>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="mb-12 bg-blue-50 rounded-2xl p-8 border border-blue-100">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-black text-slate-900 mb-3">Acknowledgment of Understanding</h3>
                <p className="text-slate-600 leading-relaxed">
                  By using the DAGGPT Platform, you acknowledge that you have read, understood, and agree to be bound by 
                  these Terms of Service. If you have any questions regarding these Terms, please contact us at 
                  <a href="mailto:support@daggpt.network" className="text-blue-600 hover:text-blue-700 font-semibold"> support@daggpt.network</a> 
                  before using the Platform.
                </p>
              </div>
            </div>
          </section>

        </div>

        {/* Back to Top Button */}
        <div className="mt-16 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-black text-white rounded-xl font-bold transition-all hover:scale-105"
          >
            Back to Top
          </button>
        </div>
      </div>
    </div>
  );
}
